import { useCallback, useEffect, useState } from 'react';
import { useGetInvoicePDF } from './service-hooks/invoice';

interface UseInvoicePDFViewerOptions {
	/**
	 * Optional: Custom invoice number to fetch (for multi-modal scenarios)
	 */
	invoiceNumberToFetch?: string;
	/**
	 * Optional: Custom clinic QBO ID to fetch (for multi-modal scenarios)
	 */
	clinicQBOIdToFetch?: string;
	/**
	 * Optional: Custom condition for when to fetch
	 */
	shouldFetch?: boolean;
}

/**
 * Custom hook for managing invoice PDF viewing functionality
 * 
 * Handles:
 * - State management for modal visibility
 * - Invoice number and clinic QBO ID tracking
 * - PDF fetching via React Query
 * - Blob URL creation and cleanup
 * 
 * @param options - Optional configuration for advanced use cases (e.g., multiple modals)
 * @returns Object containing state, handlers, and PDF viewer props
 */
export const useInvoicePDFViewer = (options?: UseInvoicePDFViewerOptions) => {
	// State for viewing invoice PDF modal
	const [viewInvoiceModal, setViewInvoiceModal] = useState<boolean>(false);
	const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string>('');
	const [selectedClinicQBOId, setSelectedClinicQBOId] = useState<string>('');
	const [pdfViewerUrl, setPdfViewerUrl] = useState<string>('');

	// Determine which values to use for fetching
	const invoiceNumberForFetch = options?.invoiceNumberToFetch ?? selectedInvoiceNumber;
	const clinicQBOIdForFetch = options?.clinicQBOIdToFetch ?? selectedClinicQBOId;
	const shouldFetchPDF = options?.shouldFetch ?? !!clinicQBOIdForFetch;

	// Minimum size for a "real" invoice PDF; smaller = empty/unavailable (avoids loading in iframe so browser never shows "Failed to load PDF document")
	const EMPTY_PDF_SIZE_THRESHOLD = 10000;

	// Fetch invoice PDF using React Query hook
	const {
		data: invoicePDFData,
		isFetching: isFetchingPDF,
		isError: isPdfLoadError,
	} = useGetInvoicePDF(
		invoiceNumberForFetch,
		clinicQBOIdForFetch,
		shouldFetchPDF,
	);

	// Empty/unavailable only after we attempted to load: no data, or PDF is too small (empty/minimal from backend)
	const dataIsEmptyOrSmall =
		invoicePDFData == null ||
		(ArrayBuffer.isView(invoicePDFData) && invoicePDFData.byteLength < EMPTY_PDF_SIZE_THRESHOLD) ||
		(invoicePDFData instanceof ArrayBuffer && invoicePDFData.byteLength < EMPTY_PDF_SIZE_THRESHOLD);
	const isEmptyOrUnavailable =
		!isFetchingPDF && shouldFetchPDF && dataIsEmptyOrSmall;

	// Create blob URL when PDF data is fetched (only for non-empty PDFs)
	useEffect(() => {
		if (invoicePDFData && !isFetchingPDF && !isEmptyOrUnavailable) {
			const blob = new Blob([invoicePDFData], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			setPdfViewerUrl(url);

			// Cleanup function to revoke object URL
			return () => {
				if (url) {
					window.URL.revokeObjectURL(url);
				}
			};
		} else if (!isFetchingPDF && isEmptyOrUnavailable) {
			setPdfViewerUrl('');
		}
	}, [invoicePDFData, isFetchingPDF, isEmptyOrUnavailable]);

	/**
	 * Reset all PDF viewer state
	 */
	const resetPDFViewer = useCallback(() => {
		setPdfViewerUrl('');
		setSelectedInvoiceNumber('');
		setSelectedClinicQBOId('');
		setViewInvoiceModal(false);
	}, []);

	return {
		// State
		viewInvoiceModal,
		pdfViewerUrl,
		selectedInvoiceNumber,
		selectedClinicQBOId,
		isFetchingPDF,
		isEmptyOrUnavailable,
		isPdfLoadError,

		// Setters
		setViewInvoiceModal,
		setPdfViewerUrl,
		setSelectedInvoiceNumber,
		setSelectedClinicQBOId,

		// Utilities
		resetPDFViewer,
	};
};
